<template>
  <base-dialog
    width="560px"
    show-close
    :visible.sync="visible"
    class="save-master-rule-dialog"
    v-ps="'.el-dialog__body'"
    v-loading="loading"
  >
    <template v-slot:header>{{ title }}</template>
    <el-form ref="form" :model="syncRule" label-width="110px" :rules="rules" label-position="left">
      <div class="form-group">
        <div class="title">基本信息</div>
        <div class="body">
          <el-form-item label="规则名称" prop="name">
            <el-input v-model="syncRule.name"></el-input>
          </el-form-item>
          <el-form-item label="规则描述" prop="description">
            <el-input v-model="syncRule.description" type="textarea"></el-input>
          </el-form-item>
        </div>
      </div>
      <div class="form-group">
        <div class="title">同步信息</div>
        <div class="body">
          <el-form-item label="工作表" prop="sheetId">
            <normal-select
              :select-list="sheets.map(sheet => ({ label: sheet.name, value: sheet.sheetId }))"
              filterable
              :selectedItem.sync="syncRule.sheetId"
              @update:selectedItem="sheetChange"
              default-label="工作表不存在"
            ></normal-select>
          </el-form-item>
          <el-form-item label="同步类型" prop="export">
            <el-select :disabled="this.isEdit" v-model="syncRule.export">
              <el-option label="接收" :value="false"></el-option>
              <el-option label="输出" :value="true"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item :label="syncRule.export ? '数据源' : '同步到'">
            <el-input disabled :value="curUser.companyName"></el-input>
          </el-form-item>
          <el-form-item v-if="!syncRule.export" label="数据源" prop="targetCId">
            <normal-select
              :select-list="syncTargets"
              filterable
              :selectedItem.sync="syncRule.targetCId"
            ></normal-select>
          </el-form-item>
          <el-form-item label="同步字段">
            <field-selector
              v-model="syncRule.rows"
              :sheet-map="sheetMap"
              :sheet-id="syncRule.sheetId"
              :exclude-readonly-control="!syncRule.export"
            ></field-selector>
          </el-form-item>
          <el-form-item label="表权限" v-if="!syncRule.export">
            <el-alert
              title="是否允许新增"
              show-icon
              :closable="false"
              type="info"
              style="line-height: 12px;"
            ></el-alert>
            <el-checkbox-group :value="checkedSheets" @input="checkSheet">
              <el-checkbox
                v-for="sheet in relatedSheets"
                :key="sheet.sheetId"
                :label="sheet.sheetId"
              >
                {{ sheet.name }}
              </el-checkbox>
            </el-checkbox-group>
          </el-form-item>
          <el-form-item label="同步条件" prop="filters">
            <condition-editor :sheet="curSheet" v-model="syncRule.filters"></condition-editor>
          </el-form-item>
        </div>
      </div>
    </el-form>
    <template v-slot:footer>
      <span class="dialog-footer">
        <base-button @click="visible = false">取消</base-button>
        <base-button type="primary" @click="submit">确认</base-button>
      </span>
    </template>
  </base-dialog>
</template>

<script>
import BaseDialog from "@/components/BaseComponents/Dialog"
import BaseButton from "@/components/UIComponents/Button"
import FieldSelector from "./FieldSelector"
import ConditionEditor from "./ConditionEditor"
import { createSyncRuleMaster, updateSyncRuleMaster } from "@/api/connect"
import { getAvailableSheets } from "@/api/sheet/sheet"
import NormalSelect from "@/components/UIComponents/NormalSelect"
import { bfs } from "@/utils/algorithm"
import { transFieldControl } from "@/utils/sheet"

export default {
  name: "save-master-rule-dialog",
  components: { NormalSelect, BaseDialog, BaseButton, FieldSelector, ConditionEditor },
  inject: ["app"],
  data() {
    return {
      visible: false,
      loading: false,
      syncRule: {},
      companies: [],
      types: [],
      rules: {
        name: [{ required: true, message: "请输入规则名称", trigger: "blur" }],
        sheetId: [{ required: true, message: "请选择工作表", trigger: "blur" }],
        export: [{ required: true, message: "请选择同步类型", trigger: "blur" }],
        rows: [{ required: true, message: "请选择同步字段", trigger: "blur" }]
      },
      debouncedRemoteMethod: null,
      searching: false,
      sheets: [],
      sheetMap: {},
      arr: []
    }
  },
  computed: {
    title() {
      return this.isEdit ? "编辑主规则" : "新建主规则"
    },
    isEdit() {
      return Boolean(this.syncRule.id)
    },
    curSheet() {
      return this.sheetMap[this.syncRule.sheetId] || {}
    },
    syncTargets() {
      return (this.curSheet.controls || [])
        .filter(c => c._type === "company")
        .map(c => ({ label: `[企业] ${c.name}`, value: c._cid }))
    },
    curUser() {
      return this.$store.getters.userInfo
    },
    checkedSheets() {
      let operateTypeMap = this.syncRule.operateTypeMap
      return this.relatedSheets
        .map(sheet => sheet.sheetId)
        .filter(sheetId => operateTypeMap[sheetId] !== "U")
    },
    controlMap() {
      return this._.chain(this.sheetMap)
        .values()
        .map("controls")
        .flatten()
        .map(c => transFieldControl(c))
        .keyBy("_cid")
        .value()
    },
    relatedSheets() {
      let sheetIdSet = new Set()
      bfs({
        arr: this.syncRule.rows || [],
        childFuc: field => field.subList,
        cbk: field => {
          let sheetId = field._parent
            ? this.controlMap[field._parent.cid]?.refSheetId
            : this.syncRule.sheetId
          sheetIdSet.add(sheetId)
        }
      })
      return Array.from(sheetIdSet)
        .map(sheetId => this.sheetMap[sheetId])
        .filter(t => t)
    }
  },
  methods: {
    open(syncRule) {
      this.syncRule = syncRule ? this._.cloneDeep(syncRule) : { export: true, operateTypeMap: {} }
      if (!this.syncRule.operateTypeMap) {
        this.$set(this.syncRule, "operateTypeMap", {})
      }
      this.visible = true
      this.loading = true
      getAvailableSheets({ appId: this.app.id, cache: false }).then(availableSheets => {
        let groupedSheets = availableSheets
        this.sheets = groupedSheets.find(app => app.id === this.app.id)?.sheets || []
        this.sheetMap = this._.chain(groupedSheets)
          .map(s => s.sheets)
          .flatten()
          .keyBy("sheetId")
          .value()
        this.loading = false
      })
    },
    checkSheet(sheetIds = []) {
      this.relatedSheets.forEach(sheet => {
        this.$set(
          this.syncRule.operateTypeMap,
          sheet.sheetId,
          sheetIds.includes(sheet.sheetId) ? "CU" : "U"
        )
      })
    },
    sheetChange() {
      this.syncRule = Object.assign({}, this.syncRule, {
        targetCId: "",
        rows: [],
        filters: []
      })
    },
    submit() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        if (this.isEdit) {
          updateSyncRuleMaster({ ...this.syncRule, isExport: this.syncRule.export }).then(() => {
            this.$message.success("操作成功")
            this.visible = false
            this.$emit("change")
          })
        } else {
          createSyncRuleMaster(
            {
              ...this.syncRule,
              isExport: this.syncRule.export
            },
            this.app.id
          ).then(() => {
            this.$message.success("操作成功")
            this.visible = false
            this.$emit("change")
          })
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.save-master-rule-dialog {
  .el-select {
    width: 100%;
  }
  ::v-deep .el-dialog {
    .el-dialog__body {
      padding: 20px;
      color: unset;
    }
  }
  .form-group {
    .title {
      font-size: 14px;
      font-weight: bold;
      color: #606266;
      margin-bottom: 14px;
    }
  }
}
</style>
